import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Layers, Lock, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { Card } from '@/components/ui';
import { Select } from '@/components/ui/select-menu';
import usePersistedState from '@/hooks/usePersistedState';
import { useProductSettings } from '@/hooks/usePriceSettings';
import { useMfboxSlabs } from '@/hooks/usePricing';
import { inr } from '@/lib/sales-metrics';
import type { MfboxPricingSlabRow, NewSlabPayload } from '@/services/pricing.service';
import { pricingService } from '@/services/pricing.service';

// ─── Constants ────────────────────────────────────────────────────────────────

const RATE_CARD_LABELS: Record<string, string> = {
  silver: 'MFBOX Silver / MFBOX Gold Legacy',
  gold_new: 'MFBOX Gold',
};

// The 12 standard MFBOX AUM band upper bounds. A slab whose (lower, upper) pair
// matches one of these is a "base" slab - it cannot be edited or deleted.
const STANDARD_BAND_SIGNATURES = new Set([
  '0|10',
  '10.01|15',
  '15.01|25',
  '25.01|50',
  '50.01|75',
  '75.01|100',
  '100.01|150',
  '150.01|200',
  '200.01|300',
  '300.01|500',
  '500|700',
  '700|1000',
]);

const isBaseSlab = (slab: MfboxPricingSlabRow): boolean =>
  STANDARD_BAND_SIGNATURES.has(`${slab.aumLowerCrores}|${slab.aumUpperCrores}`);

const todayIso = (): string => new Date().toISOString().slice(0, 10);

// ─── Types ────────────────────────────────────────────────────────────────────

interface PendingRow {
  key: string; // client-side id for React key
  rateCard: string;
  aumLowerCrores: string;
  aumUpperCrores: string;
  setupPrice: string;
  renewalPrice: string;
}

interface ConfirmState {
  title: string;
  body: string;
  onConfirm: () => void;
}

// ─── Table styles ─────────────────────────────────────────────────────────────

const TH =
  'whitespace-nowrap px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-ink/40';
const TD = 'whitespace-nowrap px-4 py-3 text-sm';

// ─── Inline input ─────────────────────────────────────────────────────────────

interface SlabInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  label: string;
}

const SlabInput = ({ value, onChange, placeholder, label }: SlabInputProps) => (
  <input
    aria-label={label}
    type="text"
    inputMode="numeric"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full rounded border border-line bg-white px-2 py-1 text-right text-sm tabular-nums focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
  />
);

// ─── Slab group table ─────────────────────────────────────────────────────────

interface SlabGroupTableProps {
  title: string;
  rateCard: string;
  slabs: MfboxPricingSlabRow[];
  pending: PendingRow[];
  editMode: boolean;
  onAddRow: (rateCard: string) => void;
  onRemovePending: (key: string) => void;
  onChangePending: (
    key: string,
    field: keyof Omit<PendingRow, 'key' | 'rateCard'>,
    value: string,
  ) => void;
  onDeleteSaved: (slab: MfboxPricingSlabRow) => void;
}

const SlabGroupTable = ({
  title,
  rateCard,
  slabs,
  pending,
  editMode,
  onAddRow,
  onRemovePending,
  onChangePending,
  onDeleteSaved,
}: SlabGroupTableProps) => (
  <div className="overflow-hidden rounded-lg border border-line">
    <div className="flex items-center justify-between border-b border-line bg-canvas px-4 py-3">
      <p className="text-sm font-semibold text-ink">{title}</p>
      {editMode && (
        <button
          type="button"
          onClick={() => onAddRow(rateCard)}
          className="flex items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-brand/90"
        >
          <Plus className="h-3 w-3" />
          Add range
        </button>
      )}
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px]">
        <thead className="border-b border-line bg-canvas/60">
          <tr>
            <th className={TH}>AUM band (Cr)</th>
            <th className={`${TH} text-right`}>Year 1 setup (₹)</th>
            <th className={`${TH} text-right`}>Renewal (₹, annual)</th>
            {editMode && <th className={`${TH} w-10`} />}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {slabs.length === 0 && pending.length === 0 ? (
            <tr>
              <td colSpan={editMode ? 4 : 3} className="px-5 py-6 text-center text-sm text-ink/50">
                No active slabs.
              </td>
            </tr>
          ) : (
            <>
              {slabs.map((slab) => {
                const base = isBaseSlab(slab);
                return (
                  <tr key={slab.id} className="hover:bg-canvas/60">
                    <td className={`${TD} font-medium text-ink`}>{slab.band}</td>
                    <td className={`${TD} text-right tabular-nums`}>{inr(slab.setupPrice)}</td>
                    <td className={`${TD} text-right tabular-nums`}>{inr(slab.renewalPrice)}</td>
                    {editMode && (
                      <td className="px-2 py-3 text-center">
                        {base ? (
                          <Lock
                            aria-label="Standard band - cannot be deleted"
                            className="mx-auto h-3.5 w-3.5 text-ink/25"
                          />
                        ) : (
                          <button
                            type="button"
                            aria-label="Delete range"
                            onClick={() => onDeleteSaved(slab)}
                            className="rounded p-1 text-red-400 transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}

              {pending.map((row) => (
                <tr key={row.key} className="bg-brand/5">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1.5">
                      <SlabInput
                        label="AUM lower"
                        value={row.aumLowerCrores}
                        onChange={(v) => onChangePending(row.key, 'aumLowerCrores', v)}
                        placeholder="Lower Cr"
                      />
                      <span className="shrink-0 text-xs text-ink/40">–</span>
                      <SlabInput
                        label="AUM upper"
                        value={row.aumUpperCrores}
                        onChange={(v) => onChangePending(row.key, 'aumUpperCrores', v)}
                        placeholder="Upper Cr"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <SlabInput
                      label="Setup price"
                      value={row.setupPrice}
                      onChange={(v) => onChangePending(row.key, 'setupPrice', v)}
                      placeholder="₹"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <SlabInput
                      label="Renewal price (annual)"
                      value={row.renewalPrice}
                      onChange={(v) => onChangePending(row.key, 'renewalPrice', v)}
                      placeholder="₹"
                    />
                  </td>
                  <td className="px-2 py-2 text-center">
                    <button
                      type="button"
                      aria-label="Remove new range"
                      onClick={() => onRemovePending(row.key)}
                      className="rounded p-1 text-ink/40 transition-colors hover:bg-canvas hover:text-ink"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// ─── Confirm dialog ───────────────────────────────────────────────────────────

interface ConfirmDialogProps {
  state: ConfirmState;
  loading: boolean;
  onCancel: () => void;
}

const ConfirmDialog = ({ state, loading, onCancel }: ConfirmDialogProps) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-sm rounded-xl border border-line bg-white p-6 shadow-xl">
      <div className="mb-4 flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
        <div>
          <p className="text-sm font-bold text-ink">{state.title}</p>
          <p className="mt-1 text-sm text-ink/60">{state.body}</p>
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-canvas disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={state.onConfirm}
          disabled={loading}
          className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand/90 disabled:opacity-50"
        >
          {loading ? 'Saving…' : 'Confirm'}
        </button>
      </div>
    </div>
  </div>
);

// ─── Validation ───────────────────────────────────────────────────────────────

const parsePositiveInt = (s: string): number | null => {
  const n = Number(s.trim());
  return Number.isFinite(n) && n >= 0 && Math.floor(n) === n ? n : null;
};

const parsePositiveDecimal = (s: string): number | null => {
  const n = Number(s.trim());
  return Number.isFinite(n) && n >= 0 ? n : null;
};

interface ValidationError {
  key: string;
  msg: string;
}

const validatePendingRows = (rows: PendingRow[]): ValidationError[] => {
  const errors: ValidationError[] = [];
  rows.forEach((row) => {
    const lower = parsePositiveDecimal(row.aumLowerCrores);
    const upper = parsePositiveDecimal(row.aumUpperCrores);
    const setup = parsePositiveInt(row.setupPrice);
    const renewal = parsePositiveInt(row.renewalPrice);
    if (lower === null) errors.push({ key: row.key, msg: 'Invalid AUM lower bound' });
    if (upper === null) errors.push({ key: row.key, msg: 'Invalid AUM upper bound' });
    if (lower !== null && upper !== null && upper <= lower) {
      errors.push({ key: row.key, msg: 'Upper bound must be greater than lower bound' });
    }
    if (setup === null) errors.push({ key: row.key, msg: 'Invalid setup price' });
    if (renewal === null) errors.push({ key: row.key, msg: 'Invalid renewal price' });
  });
  return errors;
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const AdminSlabsPage = () => {
  const queryClient = useQueryClient();
  const { data: slabs, isLoading: slabsLoading } = useMfboxSlabs();
  const { data: settings, isLoading: settingsLoading } = useProductSettings();
  const [selectedRateCard, setSelectedRateCard] = usePersistedState('adminSlabs:rateCard', '');

  const [editMode, setEditMode] = useState(false);
  // Map of rateCard -> list of unsaved new rows
  const [pendingByCard, setPendingByCard] = useState<Record<string, PendingRow[]>>({});
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [saving, setSaving] = useState(false);
  const keyCounter = useRef(0);

  const nextKey = () => {
    keyCounter.current += 1;
    return `new-${keyCounter.current}`;
  };

  const slabGroups = useMemo(() => {
    const byRateCard = new Map<string, MfboxPricingSlabRow[]>();
    (slabs ?? []).forEach((slab) => {
      const list = byRateCard.get(slab.rateCard) ?? [];
      list.push(slab);
      byRateCard.set(slab.rateCard, list);
    });

    const productByRateCard = new Map(
      (settings?.aumProducts ?? [])
        .filter((p) => p.rateCard)
        .map((p) => [p.rateCard as string, p.name]),
    );

    return Array.from(byRateCard.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([rateCard, rows]) => ({
        rateCard,
        title: productByRateCard.get(rateCard) ?? RATE_CARD_LABELS[rateCard] ?? rateCard,
        slabs: rows,
      }));
  }, [slabs, settings?.aumProducts]);

  const visibleGroups = selectedRateCard
    ? slabGroups.filter((g) => g.rateCard === selectedRateCard)
    : slabGroups;

  const allPendingRows = useMemo(() => Object.values(pendingByCard).flat(), [pendingByCard]);

  // ── Edit mode handlers ──────────────────────────────────────────────────────

  const handleEnterEdit = useCallback(() => {
    setPendingByCard({});
    setValidationErrors([]);
    setEditMode(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditMode(false);
    setPendingByCard({});
    setValidationErrors([]);
  }, []);

  const handleAddRow = useCallback((rateCard: string) => {
    const row: PendingRow = {
      key: nextKey(),
      rateCard,
      aumLowerCrores: '',
      aumUpperCrores: '',
      setupPrice: '',
      renewalPrice: '',
    };
    setPendingByCard((prev) => ({
      ...prev,
      [rateCard]: [...(prev[rateCard] ?? []), row],
    }));
    setValidationErrors([]);
  }, []);

  const handleRemovePending = useCallback((key: string) => {
    setPendingByCard((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((rc) => {
        next[rc] = next[rc].filter((r) => r.key !== key);
      });
      return next;
    });
    setValidationErrors((prev) => prev.filter((e) => e.key !== key));
  }, []);

  const handleChangePending = useCallback(
    (key: string, field: keyof Omit<PendingRow, 'key' | 'rateCard'>, value: string) => {
      setPendingByCard((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((rc) => {
          next[rc] = next[rc].map((r) => (r.key === key ? { ...r, [field]: value } : r));
        });
        return next;
      });
    },
    [],
  );

  // ── Save (add new rows) ─────────────────────────────────────────────────────

  const handleSaveClick = useCallback(() => {
    const errors = validatePendingRows(allPendingRows);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    if (allPendingRows.length === 0) {
      setEditMode(false);
      return;
    }
    const today = todayIso();
    setConfirm({
      title: `Add ${allPendingRows.length} new range${allPendingRows.length > 1 ? 's' : ''}?`,
      body: `These ranges will become immediately active (effective from ${today}). Existing slabs are not affected.`,
      onConfirm: async () => {
        setSaving(true);
        try {
          const payload: NewSlabPayload[] = allPendingRows.map((r) => ({
            rateCard: r.rateCard,
            aumLowerCrores: Number(r.aumLowerCrores),
            aumUpperCrores: Number(r.aumUpperCrores),
            setupPrice: Number(r.setupPrice),
            renewalPrice: Number(r.renewalPrice),
            effectiveFrom: today,
          }));
          await pricingService.createSlabs(payload);
          await queryClient.invalidateQueries({ queryKey: ['mfbox-slabs'] });
          setConfirm(null);
          setEditMode(false);
          setPendingByCard({});
          setValidationErrors([]);
        } finally {
          setSaving(false);
        }
      },
    });
  }, [allPendingRows, queryClient]);

  // ── Delete saved slab ────────────────────────────────────────────────────────

  const handleDeleteSaved = useCallback(
    (slab: MfboxPricingSlabRow) => {
      setConfirm({
        title: 'Delete this range?',
        body: `AUM ${slab.band} - ${inr(slab.setupPrice)} setup / ${inr(slab.renewalPrice)} renewal. This cannot be undone.`,
        onConfirm: async () => {
          setSaving(true);
          try {
            await pricingService.deleteSlabById(slab.id);
            await queryClient.invalidateQueries({ queryKey: ['mfbox-slabs'] });
            setConfirm(null);
          } finally {
            setSaving(false);
          }
        },
      });
    },
    [queryClient],
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  if (slabsLoading || settingsLoading) {
    return <p className="text-sm text-ink/50">Loading slabs…</p>;
  }

  const firstValidationError = validationErrors[0];

  return (
    <div>
      {confirm && (
        <ConfirmDialog
          state={confirm}
          loading={saving}
          onCancel={() => !saving && setConfirm(null)}
        />
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Slabs</h1>
          <p className="mt-1 text-sm text-ink/50">
            AUM-based rate cards by band. Year 1 uses the setup fee; years 2+ use renewal.
            {!editMode && ' New AUM products are added from Product Settings.'}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {editMode ? (
            <>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="rounded-lg border border-line px-3 py-1.5 text-sm font-semibold text-ink transition-colors hover:bg-canvas"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveClick}
                className="flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-brand/90"
              >
                <Save className="h-3.5 w-3.5" />
                Save changes
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleEnterEdit}
              className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-sm font-semibold text-ink transition-colors hover:bg-canvas"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit slabs
            </button>
          )}
        </div>
      </div>

      {editMode && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <span>
            <strong>Standard bands are locked.</strong> Use "Add range" to add custom AUM tiers.
            Only non-standard ranges can be deleted.
          </span>
        </div>
      )}

      {firstValidationError && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {firstValidationError.msg}
          {validationErrors.length > 1 &&
            ` (and ${validationErrors.length - 1} more error${validationErrors.length > 2 ? 's' : ''})`}
        </div>
      )}

      <Card className="mt-6 overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-4 border-b border-line px-5 py-4">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-brand" />
            <p className="text-sm font-bold text-ink">Pricing slabs</p>
          </div>

          {slabGroups.length > 1 && (
            <div className="ml-auto w-64">
              {/* The shared control, not a local <select>: it renders the same
                  styled option list every other dropdown in the app does,
                  instead of the OS-native one. It draws its own chevron. */}
              <Select
                aria-label="Filter by product"
                value={selectedRateCard}
                onChange={(e) => setSelectedRateCard(e.target.value)}
              >
                <option value="">All products</option>
                {slabGroups.map((g) => (
                  <option key={g.rateCard} value={g.rateCard}>
                    {g.title}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {selectedRateCard && (
            <button
              type="button"
              onClick={() => setSelectedRateCard('')}
              className="text-sm font-semibold text-ink/50 transition-colors hover:text-ink"
            >
              Clear
            </button>
          )}
        </div>

        {visibleGroups.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-ink/50">
            No active slabs. Run <span className="font-mono text-xs">npm run seed:slabs</span> on
            the server or add an AUM-based product from Product Settings.
          </p>
        ) : (
          <div className="grid gap-5 p-5 lg:grid-cols-2">
            {visibleGroups.map((group) => (
              <SlabGroupTable
                key={group.rateCard}
                title={group.title}
                rateCard={group.rateCard}
                slabs={group.slabs}
                pending={pendingByCard[group.rateCard] ?? []}
                editMode={editMode}
                onAddRow={handleAddRow}
                onRemovePending={handleRemovePending}
                onChangePending={handleChangePending}
                onDeleteSaved={handleDeleteSaved}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminSlabsPage;
